
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { CheckCircle, AlertTriangle, Bot, Users, Activity, Phone } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { 
  isBenchmarkBotConfigured, 
  getBenchmarkBotStats,
  getAllBenchmarkBotConnections,
  getPhoneVerifications
} from '@/services/telegram/benchmarkBotService';
import { BenchmarkBotWebhookStatus } from './BenchmarkBotWebhookStatus';

const BenchmarkBotManager: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isBotConfigured, setIsBotConfigured] = useState(false);
  const [stats, setStats] = useState({
    totalConnections: 0,
    totalEntriesToday: 0,
    totalPhoneVerifications: 0,
    verifiedToday: 0
  });
  const [connections, setConnections] = useState([]);
  const [phoneVerifications, setPhoneVerifications] = useState([]);
  const { toast } = useToast();

  const checkBotConfiguration = async () => {
    try {
      const configured = await isBenchmarkBotConfigured();
      setIsBotConfigured(configured);
      
      if (configured) {
        const [statsData, connectionsData, verificationsData] = await Promise.all([
          getBenchmarkBotStats(),
          getAllBenchmarkBotConnections(),
          getPhoneVerifications()
        ]);
        
        setStats(statsData);
        setConnections(connectionsData);
        setPhoneVerifications(verificationsData);
      }
    } catch (error) {
      console.error('Error checking bot configuration:', error);
    }
  };

  const refreshData = async () => {
    setIsLoading(true);
    await checkBotConfiguration();
    setIsLoading(false);
  };

  useEffect(() => {
    refreshData();
  }, []);

  if (!isBotConfigured) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bot className="h-5 w-5" />
            Benchmark Bot Manager
          </CardTitle>
          <CardDescription>
            Manage the Telegram bot for benchmark data entry
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Bot Not Configured</AlertTitle>
            <AlertDescription>
              The benchmark bot token is not configured. Please contact your administrator to set up the TELEGRAM_BENCHMARK_BOT_TOKEN.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bot className="h-5 w-5" />
            Benchmark Bot Manager
          </CardTitle>
          <CardDescription>
            Manage the Telegram bot for benchmark data entry
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Alert>
            <CheckCircle className="h-4 w-4" />
            <AlertTitle>Bot Configured</AlertTitle>
            <AlertDescription>
              The benchmark bot is properly configured and ready to use.
            </AlertDescription>
          </Alert>
          
          <div className="mt-4">
            <h3 className="text-lg font-medium mb-4">How to Use the Benchmark Bot</h3>
            <div className="space-y-2 text-sm text-muted-foreground">
              <p>1. Search for the benchmark bot on Telegram</p>
              <p>2. Start a conversation with the bot by sending /start</p>
              <p>3. Share your phone number when prompted</p>
              <p>4. Your phone number must be registered in your user profile with benchmark access</p>
              <p>5. Once verified, you can use the bot to enter price data</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <BenchmarkBotWebhookStatus />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Connections</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalConnections}</div>
            <p className="text-xs text-muted-foreground">
              Users connected to bot
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Entries Today</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalEntriesToday}</div>
            <p className="text-xs text-muted-foreground">
              Price entries submitted today
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Phone Verifications</CardTitle>
            <Phone className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalPhoneVerifications}</div>
            <p className="text-xs text-muted-foreground">
              Total verification attempts
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Verified Today</CardTitle>
            <CheckCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.verifiedToday}</div>
            <p className="text-xs text-muted-foreground">
              Users verified today
            </p>
          </CardContent>
        </Card>
      </div>

      {connections.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Recent Connections</CardTitle>
            <CardDescription>
              Users who have connected to the benchmark bot
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {connections.slice(0, 5).map((connection: any) => (
                <div key={connection.id} className="flex items-center justify-between p-2 border rounded">
                  <div>
                    <div className="font-medium">
                      {connection.telegram_username ? `@${connection.telegram_username}` : `User ${connection.telegram_user_id}`}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Connected: {new Date(connection.created_at).toLocaleDateString()}
                    </div>
                  </div>
                  <div className={`px-2 py-1 rounded text-xs ${
                    connection.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                  }`}>
                    {connection.is_active ? 'Active' : 'Inactive'}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="flex justify-end">
        <Button onClick={refreshData} disabled={isLoading} variant="outline">
          {isLoading ? 'Refreshing...' : 'Refresh Data'}
        </Button>
      </div>
    </div>
  );
};

export default BenchmarkBotManager;
